import { IObservable, Subject } from '~/rage/utils';
import {
	ClothesType,
	equipmentSlotByClothesType,
	HeldItem,
	IClientConfigs,
	InventoryType,
	Item,
	ItemAction,
	ItemData,
	ItemType,
	PackedInventory,
} from '~/shared';

import { devSimulatePing } from '~/rage/decorators';
import { IInventoryService } from './inventoryServiceTypes';

const mockItems: Omit<Item, 'id'>[] = [
	{
		itemId: 'ccl_m_164',
		name: 'Шорты',
		subName: 'Essentials',
		count: 1,
		type: ItemType.CLOTHES,
		image: '',
		availableActions: [ItemAction.EQUIP],
		stackable: false,
		tags: [],
		data: {
			clothes: {
				type: ClothesType.PANTS,
			},
		},
	},
	{
		itemId: 'ccl_m_155',
		name: 'Куртка',
		subName: 'Tactical',
		count: 1,
		type: ItemType.CLOTHES,
		image: '',
		availableActions: [ItemAction.EQUIP],
		stackable: false,
		tags: [],
		data: {
			clothes: {
				type: ClothesType.TOP,
			},
		},
	},
	{
		itemId: 'backpack',
		name: '«Adidas»',
		subName: '«Adidas»',
		count: 1,
		type: ItemType.BACKPACK,
		availableActions: [ItemAction.EQUIP, ItemAction.DROP],
		stackable: false,
		slots: 5,
		tags: [],
		data: {
			backpack: {
				inventoryId: 'backpack-1',
			},
			clothes: {
				type: ClothesType.BACKPACK,
			},
		},
	},
	{
		itemId: 'bread',
		name: 'Рыба',
		subName: 'Свежая',
		count: 5,
		type: ItemType.FOOD,
		availableActions: [ItemAction.USE, ItemAction.DROP],
		stackable: true,
		tags: [],
	},
	{
		itemId: 'axe',
		name: 'Торор',
		subName: '«Кредитка»',
		count: 1,
		type: ItemType.OTHER,
		availableActions: [ItemAction.USE, ItemAction.DROP, ItemAction.GIVE_HANDS],
		stackable: false,
		tags: [],
	},
];

const mockItemsWithId: Item[] = [
	{
		...mockItems[0],
		id: 'pants-0',
	},
	{
		...mockItems[1],
		id: 'backpack-1-0',
	},
	{
		...mockItems[2],
		id: 'fish-0',
	},
	{
		...mockItems[3],
		id: 'jacket-0',
	},
	{
		...mockItems[4],
		id: 'knife-credit-0',
	},
	{
		...mockItems[4],
		id: 'knife-credit-1',
	},
];

const mockCharacterInventories: {
	[InventoryType.EQUIPMENT]: PackedInventory;
	[InventoryType.HOTBAR]: PackedInventory;
	[InventoryType.PLAYER]: PackedInventory;
} = {
	equipment: {
		id: '2d1ea96e-ac13-471e-a6f7-0105cdfdffe8',
		type: InventoryType.EQUIPMENT,
		ownerId: '36',
		capacity: 13,
		items: [
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
			null,
		],
		isTemporary: false,
		isInfinite: false,
	},
	hotbar: {
		id: '2ead1986-abdd-4dcc-aac5-b430a06c57d5',
		type: InventoryType.HOTBAR,
		ownerId: '36',
		capacity: 4,
		items: [null, null, null, null],
		isTemporary: false,
		isInfinite: false,
	},
	player: {
		id: 'e1b5f6c8-430c-4e46-9cdc-5ddf23eefa40',
		type: InventoryType.PLAYER,
		ownerId: '36',
		capacity: 20,
		items: [
			...mockItemsWithId.map((item) => ({
				id: item.id,
				count: item.count,
				data: item.data,
			})),
			...new Array(20 - mockItemsWithId.length).fill(null),
		],
		isTemporary: false,
		isInfinite: false,
	},
};

const inventoryExchange: PackedInventory = {
	id: 'exchange',
	type: InventoryType.EXCHANGE,
	ownerId: 'player',
	capacity: 10,
	items: new Array(10).fill(null),
	isTemporary: true,
	isInfinite: false,
};

const inventoryVehicle: PackedInventory = {
	id: 'vehicle',
	type: InventoryType.VEHICLE,
	ownerId: 'player',
	capacity: 50,
	items: new Array(50).fill(null),
	isTemporary: false,
	isInfinite: false,
};

const inventoryDropped: PackedInventory = {
	id: 'dropped',
	type: InventoryType.DROPPED,
	ownerId: 'player',
	capacity: 10,
	items: [
		mockItemsWithId[1],
		mockItemsWithId[3],
		...new Array(10 - 2).fill(null),
	],
	isTemporary: true,
	isInfinite: true,
};

const inventoryHouse: PackedInventory = {
	id: 'house',
	type: InventoryType.HOUSE,
	ownerId: 'player',
	capacity: 16,
	items: new Array(16).fill(null),
	isTemporary: false,
	isInfinite: false,
};

const createBackpackInventory = (id: string): PackedInventory => ({
	id,
	type: InventoryType.BACKPACK,
	ownerId: 'player',
	capacity: 10,
	items: new Array(10).fill(null),
	isTemporary: false,
	isInfinite: false,
});

export class InventoryServiceStub implements IInventoryService {
	private inventoryIds: Subject<Record<InventoryType, string>> = new Subject({
		[InventoryType.PLAYER]: '',
		[InventoryType.EQUIPMENT]: '',
		[InventoryType.HOTBAR]: '',
		[InventoryType.BACKPACK]: 'backpack-1',
		[InventoryType.EXCHANGE]: 'exchange',
		[InventoryType.VEHICLE]: 'vehicle',
		[InventoryType.HOUSE]: 'house',
		[InventoryType.DROPPED]: 'dropped',
	});

	private inventories: Record<InventoryType, Subject<PackedInventory | null>> =
		{
			[InventoryType.PLAYER]: new Subject<PackedInventory | null>(null),
			[InventoryType.EQUIPMENT]: new Subject<PackedInventory | null>(null),
			[InventoryType.HOTBAR]: new Subject<PackedInventory | null>(null),
			[InventoryType.BACKPACK]: new Subject<PackedInventory | null>(null),
			[InventoryType.VEHICLE]: new Subject<PackedInventory | null>(null),
			[InventoryType.HOUSE]: new Subject<PackedInventory | null>(null),
			[InventoryType.EXCHANGE]: new Subject<PackedInventory | null>(null),
			[InventoryType.DROPPED]: new Subject<PackedInventory | null>(null),
		};

	private backpack = new Subject(true);
	private isExchange = new Subject(false);
	private secondaryInventoryId = new Subject<string | null>('vehicle');
	private secondaryInventoryType = new Subject<
		InventoryType.VEHICLE | InventoryType.HOUSE | InventoryType.DROPPED | null
	>(InventoryType.VEHICLE);
	private secondaryInventoryTitle = new Subject<string | null>(
		'Mercedes E63s AMG PRO MAX',
	);
	private exchangeInventoriesIds = new Subject<{
		fromInventoryId: string;
		toInventoryId: string;
	}>({ fromInventoryId: 'exchange1', toInventoryId: 'exchange2' });
	private exchangeInventories = {
		fromInventoryType: new Subject<PackedInventory | null>(null),
		toInventoryType: new Subject<PackedInventory | null>(null),
	};
	private exchangeFromMoneyAmount = new Subject<number>(0);
	private exchangeToMoneyAmount = new Subject<number>(0);
	private isSecondPlayerReady = new Subject<boolean>(false);
	private exchangeDelaySec = new Subject<number>(5);
	private isExchangeCompleted = new Subject<boolean>(false);
	private otherPlayerInfo = new Subject<{
		nickname: string;
		level: number;
		serverPlayerId: number;
	}>({
		nickname: 'Dmitrii Stepanov',
		level: 38,
		serverPlayerId: 52,
	});
	private inventorySlotsCount = new Subject<{
		available: number;
		total: number;
	}>({ available: 10, total: 20 });
	private maxExchangeMoneyAmount = new Subject<number>(10000);

	private activeItems = new Subject<HeldItem[]>([
		{ inventoryType: InventoryType.PLAYER, itemSlot: 5 },
	]);

	private readonly inventoryTypesById: Map<string, InventoryType> = new Map();
	private readonly unknownSubjects: Map<
		string,
		Subject<PackedInventory | null>
	> = new Map();
	private readonly inventoryItems = new Subject<
		IClientConfigs['inventory']['items']
	>([
		{
			id: 'medkit',
			name: 'Аптечка',
			type: ItemType.OTHER,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 10,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'adrenaline',
			name: 'Адреналин',
			type: ItemType.OTHER,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 5,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'repair_kit',
			name: 'Ремкомплект',
			type: ItemType.OTHER,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 10,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'fuel_canister',
			name: 'Канистра с топливом',
			type: ItemType.OTHER,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 5,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'bread',
			name: 'Хлеб',
			type: ItemType.FOOD,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 20,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'water',
			name: 'Вода',
			type: ItemType.FOOD,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 20,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'energy_drink',
			name: 'Энергетик',
			type: ItemType.FOOD,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 10,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
		{
			id: 'burger',
			name: 'Бургер',
			type: ItemType.FOOD,
			isUsable: true,
			isDroppable: true,
			isTradeable: true,
			isHoldable: true,
			isStackable: true,
			maxStackSize: 10,
			isBreakable: false,
			maxDurability: null,
			isBackpack: false,
			slotsCount: null,
			isClothes: false,
			clothesType: null,
		},
	]);

	constructor() {
		this.inventories[InventoryType.PLAYER].next(
			mockCharacterInventories.player,
		);
		this.inventories[InventoryType.EQUIPMENT].next(
			mockCharacterInventories.equipment,
		);
		this.inventories[InventoryType.HOTBAR].next(
			mockCharacterInventories.hotbar,
		);

		this.inventoryIds.next({
			...this.inventoryIds.getValue(),
			[InventoryType.PLAYER]: mockCharacterInventories.player.id,
			[InventoryType.EQUIPMENT]: mockCharacterInventories.equipment.id,
			[InventoryType.HOTBAR]: mockCharacterInventories.hotbar.id,
		});

		this.inventories[InventoryType.BACKPACK].next(
			createBackpackInventory('backpack-1'),
		);

		this.inventoryIds.subscribe((mapping) => {
			this.inventoryTypesById.clear();
			(Object.entries(mapping) as [string, string][]).forEach(
				([typeKey, id]) => {
					if (id) {
						this.inventoryTypesById.set(
							id,
							typeKey as unknown as InventoryType,
						);
					}
				},
			);
		});

		// this.secondaryInventoryId.subscribe((id) => {
		// 	if (id) {
		// 		this.requestInventory(id);
		// 	}
		// });

		// setTimeout(() => {
		// 	this.handleUpdate({
		// 		type: InventoryType.VEHICLE,
		// 		inventory: {
		// 			...inventoryVehicle,
		// 			type: InventoryType.VEHICLE,
		// 		},
		// 	});
		// }, 5000);

		setTimeout(() => {
			this.handleUpdate({
				type: InventoryType.DROPPED,
				inventory: inventoryDropped,
			});
		}, 5000);

		// setTimeout(() => {
		// 	this.handleUpdate({
		// 		type: InventoryType.DROPPED,
		// 		inventory: null,
		// 	});
		// }, 10000);

		// setTimeout(() => {
		// 	this.handleUpdate({
		// 		type: InventoryType.EXCHANGE,
		// 		inventory: fromExchangeInventory,
		// 	});
		// 	this.handleUpdate({
		// 		type: InventoryType.EXCHANGE,
		// 		inventory: toExchangeInventory,
		// 	});
		// 	this.exchangeToMoneyAmount.next(1000);
		// }, 10000);
	}

	private handleUpdate(payload: {
		type: InventoryType;
		inventory: PackedInventory | null;
	}) {
		const { type, inventory } = payload;
		console.log('inventory: handleUpdate', type, inventory);

		if (
			type === InventoryType.EQUIPMENT ||
			type === InventoryType.HOTBAR ||
			type === InventoryType.PLAYER
		) {
			this.inventories[type].next(inventory);
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[type]: inventory?.id ?? '',
			});

			return;
		}

		if (type === InventoryType.BACKPACK) {
			if (inventory) {
				this.inventories[InventoryType.BACKPACK].next(inventory);
				this.inventoryIds.next({
					...this.inventoryIds.getValue(),
					[InventoryType.BACKPACK]: inventory?.id ?? '',
				});
				this.backpack.next(true);

				return;
			}
			this.backpack.next(false);
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[InventoryType.BACKPACK]: '',
			});

			return;
		}

		if (type === InventoryType.EXCHANGE) {
			if (!inventory) {
				this.exchangeInventories.fromInventoryType.next(null);
				this.exchangeInventories.toInventoryType.next(null);
				this.exchangeToMoneyAmount.next(0);
				this.isSecondPlayerReady.next(false);
				return;
			}

			const characterId = '123';

			if (inventory?.ownerId === characterId) {
				this.exchangeInventories.fromInventoryType.next(inventory);
			} else {
				this.exchangeInventories.toInventoryType.next(inventory);
			}
		}

		if (
			type === InventoryType.VEHICLE ||
			type === InventoryType.HOUSE ||
			type === InventoryType.DROPPED
		) {
			this.secondaryInventoryType.next(
				type as
					| InventoryType.VEHICLE
					| InventoryType.HOUSE
					| InventoryType.DROPPED,
			);
			this.secondaryInventoryId.next(inventory?.id ?? '');
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[type]: inventory?.id ?? '',
			});
			this.inventories[type].next(inventory);

			return;
		}
	}

	public getInventoriesId(): IObservable<Record<InventoryType, string>> {
		return this.inventoryIds;
	}

	public getInventory(id: string): Subject<PackedInventory | null> {
		if (id === '') {
			return this.inventories[InventoryType.BACKPACK];
		}
		const inventoryType = this.inventoryTypesById.get(id);
		if (inventoryType !== undefined) {
			return this.inventories[inventoryType];
		}
		if (!this.unknownSubjects.has(id)) {
			this.unknownSubjects.set(id, new Subject<PackedInventory | null>(null));
		}
		return this.unknownSubjects.get(id)!;
	}

	@devSimulatePing()
	public requestInventory(id: string): Promise<void> {
		console.log('inventory: requestInventory', id);

		if (
			id === 'vehicle' &&
			!this.inventories[InventoryType.VEHICLE].getValue()
		) {
			this.inventories[InventoryType.VEHICLE].next({ ...inventoryVehicle });
		}
		if (id === 'house' && !this.inventories[InventoryType.HOUSE].getValue()) {
			this.inventories[InventoryType.HOUSE].next({ ...inventoryHouse });
		}
		if (
			id === 'exchange' &&
			!this.inventories[InventoryType.EXCHANGE].getValue()
		) {
			this.inventories[InventoryType.EXCHANGE].next({ ...inventoryExchange });
		}

		if (id.startsWith('backpack')) {
			this.inventoryIds.next({
				...this.inventoryIds.getValue(),
				[InventoryType.BACKPACK]: id,
			});
			if (!this.inventories[InventoryType.BACKPACK].getValue()) {
				this.inventories[InventoryType.BACKPACK].next(
					createBackpackInventory(id),
				);
			}
			console.log(
				'backpackInventory',
				this.inventories[InventoryType.BACKPACK].getValue(),
			);
			this.backpack.next(true);
			return Promise.resolve();
		}
		return Promise.resolve();
	}

	@devSimulatePing()
	public getInventoryById(id: string): Promise<PackedInventory | null> {
		const inventoryType = this.inventoryTypesById.get(id);
		if (inventoryType !== undefined) {
			return Promise.resolve(
				this.inventories[inventoryType]?.getValue() ?? null,
			);
		}
		return Promise.resolve(null);
	}

	private updateBackpackFlag(inv: PackedInventory | null) {
		if (!inv || inv.type !== InventoryType.EQUIPMENT) return;
		const backpackSlot = equipmentSlotByClothesType[ClothesType.BACKPACK];
		const hasBp = !!inv.items[backpackSlot];
		this.backpack.next(hasBp);
	}

	public getFromExchangeInventory(): IObservable<PackedInventory | null> {
		return this.exchangeInventories.fromInventoryType;
	}

	public getToExchangeInventory(): IObservable<PackedInventory | null> {
		return this.exchangeInventories.toInventoryType;
	}

	public getIsExchange(): IObservable<boolean> {
		return this.isExchange;
	}

	public getMaxExchangeMoneyAmount(): IObservable<number> {
		return this.maxExchangeMoneyAmount;
	}

	public getOtherPlayerInfo(): IObservable<{
		nickname: string;
		level: number;
		serverPlayerId: number;
	}> {
		return this.otherPlayerInfo;
	}

	@devSimulatePing()
	public async moveItem(
		inventoryId: string,
		itemId: string,
		itemCount: number,
		itemData: ItemData | undefined,
		itemSlotFrom: number,
		itemSlotTo: number,
	): Promise<void> {
		const invSubject = this.getInventory(inventoryId);
		const current = invSubject.getValue();
		if (!current) return;
		const inventory: PackedInventory = { ...current } as PackedInventory;
		if (itemSlotFrom < 0 || itemSlotFrom >= inventory.items.length) return;
		if (itemSlotTo < 0 || itemSlotTo >= inventory.items.length) return;

		const fromItem = inventory.items[itemSlotFrom];
		if (!fromItem || fromItem.id !== itemId) return;

		const toItem = inventory.items[itemSlotTo];

		if (
			inventory.type === InventoryType.BACKPACK &&
			this.getItemTypeById(itemId) === ItemType.BACKPACK
		) {
			return;
		}

		const fromBaseId = this.getBaseItemId(fromItem.id);
		const toBaseId = toItem ? this.getBaseItemId(toItem.id) : undefined;

		if (
			toItem &&
			toItem.id !== fromItem.id &&
			toBaseId === fromBaseId &&
			this.isStackable(fromItem.id) &&
			this.isStackable(toItem.id)
		) {
			const moveAmount = Math.min(itemCount, fromItem.count);
			toItem.count += moveAmount;
			if (moveAmount >= fromItem.count) {
				inventory.items[itemSlotFrom] = null;
			} else {
				fromItem.count -= moveAmount;
			}
		} else {
			const moveAmount = Math.min(itemCount, fromItem.count);
			const movedItem = {
				...fromItem,
				id:
					moveAmount === fromItem.count
						? fromItem.id
						: `${fromItem.id}-split-${Date.now()}`,
				count: moveAmount,
			};

			if (moveAmount === fromItem.count) {
				inventory.items[itemSlotTo] = movedItem;
				inventory.items[itemSlotFrom] = toItem ? { ...toItem } : null;
			} else {
				inventory.items[itemSlotTo] = movedItem;
				fromItem.count -= moveAmount;
			}
		}

		invSubject.next(inventory);
		this.updateBackpackFlag(inventory);
		console.log(
			'inventory: moveItem',
			itemId,
			itemCount,
			itemSlotFrom,
			itemSlotTo,
		);
		this.requestInventory(inventoryId);
	}

	@devSimulatePing()
	public async transferItem(
		itemId: string,
		itemCount: number,
		itemData: ItemData | undefined,
		inventoryIdFrom: string,
		inventorySlotFrom: number,
		inventoryIdTo: string,
		inventorySlotTo?: number,
	): Promise<void> {
		console.log(
			'inventory: transferItem',
			itemId,
			itemCount,
			inventoryIdFrom,
			inventorySlotFrom,
			inventoryIdTo,
			inventorySlotTo,
		);
		const autoDetermineSlot = (
			inv: PackedInventory,
			baseItemId: string,
		): number => {
			const stackIdx = inv.items.findIndex(
				(st) =>
					st &&
					this.getBaseItemId(st.id) === baseItemId &&
					this.isStackable(st.id),
			);
			if (stackIdx !== -1) return stackIdx;

			return inv.items.findIndex((st) => st === null);
		};

		if (inventorySlotTo === undefined || inventorySlotTo === -1) {
			const toInvSubj = this.getInventory(inventoryIdTo);
			const toInv = toInvSubj.getValue();
			if (toInv) {
				inventorySlotTo = autoDetermineSlot(
					toInv,
					this.getBaseItemId(itemId) || '',
				);
			}
		}

		if (inventorySlotTo === -1 || inventorySlotTo === undefined) {
			return;
		}

		if (inventoryIdFrom === inventoryIdTo) {
			this.moveItem(
				inventoryIdFrom,
				itemId,
				itemCount,
				itemData,
				inventorySlotFrom,
				inventorySlotTo,
			);
			return;
		}

		const fromSubject = this.getInventory(inventoryIdFrom);
		const toSubject = this.getInventory(inventoryIdTo);

		const fromCurrent = fromSubject.getValue();
		const toCurrent = toSubject.getValue();
		if (!fromCurrent || !toCurrent) return;
		const fromInv: PackedInventory = { ...fromCurrent } as PackedInventory;
		const toInv: PackedInventory = { ...toCurrent } as PackedInventory;

		if (inventorySlotFrom < 0 || inventorySlotFrom >= fromInv.items.length)
			return;
		if (inventorySlotTo < 0 || inventorySlotTo >= toInv.items.length) return;

		const itemFrom = fromInv.items[inventorySlotFrom];
		if (!itemFrom || itemFrom.id !== itemId) return;

		if (
			toInv.type === InventoryType.BACKPACK &&
			this.getItemTypeById(itemId) === ItemType.BACKPACK
		) {
			return;
		}

		const targetItem = toInv.items[inventorySlotTo];

		const targetBaseId = targetItem
			? this.getBaseItemId(targetItem.id)
			: undefined;
		const itemFromBaseId = this.getBaseItemId(itemFrom.id);

		const moveAmount = Math.min(itemCount, itemFrom.count);

		if (
			targetItem &&
			targetBaseId === itemFromBaseId &&
			this.isStackable(itemFrom.id) &&
			this.isStackable(targetItem.id)
		) {
			targetItem.count += moveAmount;
			if (moveAmount >= itemFrom.count) {
				fromInv.items[inventorySlotFrom] = null;
			} else {
				itemFrom.count -= moveAmount;
			}
		} else if (!targetItem) {
			const movedItem = {
				...itemFrom,
				id:
					moveAmount === itemFrom.count
						? itemFrom.id
						: `${itemFrom.id}-split-${Date.now()}`,
				count: moveAmount,
			};
			toInv.items[inventorySlotTo] = movedItem;
			if (moveAmount >= itemFrom.count) {
				fromInv.items[inventorySlotFrom] = null;
			} else {
				itemFrom.count -= moveAmount;
			}
		} else {
			if (moveAmount < itemFrom.count) {
				return;
			}
			fromInv.items[inventorySlotFrom] = { ...targetItem };
			toInv.items[inventorySlotTo] = { ...itemFrom };
		}

		fromSubject.next(fromInv);
		toSubject.next(toInv);
		this.updateBackpackFlag(fromInv);
		this.updateBackpackFlag(toInv);
		console.log(
			'inventory: transferItem',
			itemId,
			itemCount,
			inventoryIdFrom,
			inventorySlotFrom,
			inventoryIdTo,
			inventorySlotTo,
		);
		this.requestInventory(inventoryIdFrom);
		this.requestInventory(inventoryIdTo);
	}

	@devSimulatePing()
	public async performItemAction(
		inventoryId: string,
		itemId: string,
		_itemCount: number,
		_itemData: ItemData | undefined,
		_itemSlot: number,
		action: ItemAction,
	): Promise<void> {
		const sourceSubject = this.getInventory(inventoryId);
		const sourceInv = sourceSubject.getValue();
		if (!sourceInv) return;

		const srcIndex = sourceInv.items.findIndex((it) => it?.id === itemId);
		if (srcIndex === -1) return;
		const srcItem = sourceInv.items[srcIndex]!;

		const fullItem = this.getItemById(itemId);
		if (!fullItem) return;

		const updateSubject = (
			subject: Subject<PackedInventory | null>,
			inv: PackedInventory,
		) => {
			subject.next({ ...inv });
		};

		const findEmptySlot = (inv: PackedInventory): number =>
			inv.items.findIndex((c) => c === null);

		const addToInventory = (inv: PackedInventory, addItem: typeof srcItem) => {
			const baseId = this.getBaseItemId(addItem.id);
			if (baseId && this.isStackable(addItem.id)) {
				const stackIdx = inv.items.findIndex(
					(it) =>
						it &&
						this.getBaseItemId(it.id) === baseId &&
						this.isStackable(it.id),
				);
				if (stackIdx !== -1) {
					inv.items[stackIdx]!.count += addItem.count;
					return;
				}
			}
			const emptyIdx = findEmptySlot(inv);
			if (emptyIdx !== -1) {
				inv.items[emptyIdx] = { ...addItem };
			}
		};

		console.log('performItemAction', action, inventoryId);

		switch (action) {
			case ItemAction.DROP: {
				sourceInv.items[srcIndex] = null;

				const idxMock = mockItemsWithId.findIndex((m) => m.id === srcItem.id);
				if (idxMock !== -1) mockItemsWithId.splice(idxMock, 1);

				updateSubject(sourceSubject, { ...sourceInv });
				break;
			}
			case ItemAction.SPLIT: {
				if (srcItem.count <= 1) break;
				const half1 = Math.ceil(srcItem.count / 2);
				const half2 = srcItem.count - half1;
				srcItem.count = half1;
				const newItemId = `${srcItem.id}-split-${Date.now()}`;
				const baseMockId = srcItem.id.split('-split-')[0].split('-equip-')[0];
				const baseMockItem = mockItemsWithId.find((m) => m.id === baseMockId);
				// формируем новый стек с тем же itemId и data
				const newItemStack = {
					id: newItemId,
					itemId: baseMockItem
						? baseMockItem.itemId
						: this.getBaseItemId(srcItem.id),
					count: half2,
					data: srcItem.data,
				};

				if (baseMockItem) {
					mockItemsWithId.push({
						...baseMockItem,
						id: newItemId,
						count: half2,
					});
				}

				const emptyIdx = sourceInv.items.findIndex((c) => c === null);
				if (emptyIdx !== -1) {
					sourceInv.items[emptyIdx] = newItemStack as any;
				} else {
					srcItem.count += half2;
					break;
				}

				updateSubject(sourceSubject, sourceInv);
				break;
			}
			case ItemAction.EQUIP: {
				if (
					!(
						fullItem.type === ItemType.CLOTHES ||
						fullItem.type === ItemType.BACKPACK
					)
				)
					break;
				const clothesType: ClothesType | undefined =
					fullItem.data?.clothes?.type ??
					(fullItem.type === ItemType.BACKPACK
						? ClothesType.BACKPACK
						: undefined);
				if (clothesType === undefined) break;

				const slotIdx = equipmentSlotByClothesType[clothesType];
				const equipmentSubject = this.inventories[InventoryType.EQUIPMENT];
				const equipmentInv = {
					...equipmentSubject.getValue()!,
				} as PackedInventory;

				const equippedItem = { ...srcItem, count: 1 };

				if (srcItem.count > 1) {
					srcItem.count -= 1;
				} else {
					sourceInv.items[srcIndex] = null;
				}

				const prevItem = equipmentInv.items[slotIdx];
				if (prevItem) {
					addToInventory(sourceInv, { ...prevItem });
				}
				equipmentInv.items[slotIdx] = equippedItem;

				updateSubject(sourceSubject, sourceInv);
				equipmentSubject.next(equipmentInv);

				if (fullItem.type === ItemType.BACKPACK) {
					this.backpack.next(true);
				}

				break;
			}
			case ItemAction.UNEQUIP: {
				if (sourceInv.type !== InventoryType.EQUIPMENT) break;
				const playerSubject = this.inventories[InventoryType.PLAYER];
				const playerInv = { ...playerSubject.getValue()! } as PackedInventory;

				const equipmentSubjectLocal = this.inventories[InventoryType.EQUIPMENT];
				const equipmentInv = {
					...equipmentSubjectLocal.getValue()!,
				} as PackedInventory;
				equipmentInv.items[srcIndex] = null;

				addToInventory(playerInv, { ...srcItem });

				playerSubject.next(playerInv);
				equipmentSubjectLocal.next(equipmentInv);

				if (fullItem.type === ItemType.BACKPACK) {
					this.backpack.next(false);
					this.inventoryIds.next({
						...this.inventoryIds.getValue(),
						[InventoryType.BACKPACK]: '',
					});
					this.inventories[InventoryType.BACKPACK].next(null);
				}

				break;
			}
			case ItemAction.USE: {
				console.log('USE item', itemId);
				break;
			}
		}
		this.requestInventory(inventoryId);
		if (action === ItemAction.EQUIP && fullItem.type === ItemType.BACKPACK) {
			const bpInvId = (fullItem.data as any)?.inventoryId;
			if (bpInvId) {
				this.inventoryIds.next({
					...this.inventoryIds.getValue(),
					[InventoryType.BACKPACK]: bpInvId,
				});
				this.requestInventory(bpInvId);
			}
		}
	}

	private getItemTypeById(id: string): ItemType | undefined {
		const it = mockItemsWithId.find((m) => m.id === id);
		return it?.type;
	}

	private isStackable(id: string): boolean {
		const it = mockItemsWithId.find((m) => m.id === id);
		return Boolean(it?.stackable);
	}

	private getBaseItemId(id: string): string | undefined {
		const it = mockItemsWithId.find((m) => m.id === id);
		return it?.itemId;
	}

	public hasBackpack(): IObservable<boolean> {
		return this.backpack;
	}

	private getItemById(id: string): Item | undefined {
		return mockItemsWithId.find((it) => it.id === id);
	}

	public getSecondaryInventoryId(): IObservable<string | null> {
		return this.secondaryInventoryId;
	}

	public getSecondaryInventoryType(): IObservable<
		InventoryType.VEHICLE | InventoryType.HOUSE | InventoryType.DROPPED | null
	> {
		return this.secondaryInventoryType;
	}

	public getSecondaryInventoryTitle(): IObservable<string | null> {
		return this.secondaryInventoryTitle;
	}

	public getItemInfoById(id: string): Partial<Item> | null {
		return mockItemsWithId.find((it) => it.id === id) ?? null;
	}

	public getInventoryByType(
		type: InventoryType,
	): IObservable<PackedInventory | null> {
		return this.inventories[type];
	}

	public getExchangeMoneyAmount(): IObservable<number> {
		return this.exchangeToMoneyAmount;
	}

	public async setExchangeMoneyAmount(amount: number): Promise<number> {
		console.log('setExchangeMoneyAmount', amount);
		this.exchangeFromMoneyAmount.next(amount);
		return amount;
	}

	private isPlayerReady = new Subject(false);
	public getIsPlayerReady(): IObservable<boolean> {
		return this.isPlayerReady;
	}
	public async setIsPlayerReady(isReady: boolean): Promise<void> {
		this.isPlayerReady.next(isReady);
		console.log('setIsPlayerReady', isReady);

		if (isReady) {
			setTimeout(() => {
				this.isSecondPlayerReady.next(true);
			}, 1000);
		}
	}

	public getIsSecondPlayerReady(): IObservable<boolean> {
		return this.isSecondPlayerReady;
	}

	public getExchangeDelaySec(): IObservable<number> {
		return this.exchangeDelaySec;
	}

	public async setIsPlayerConfirmed(isConfirmed: boolean): Promise<void> {
		if (isConfirmed) {
			setTimeout(() => {
				this.isExchangeCompleted.next(true);
			}, 1000);
		}
		console.log('setIsPlayerConfirmed', isConfirmed);
	}

	public getIsExchangeCompleted(): IObservable<boolean> {
		return this.isExchangeCompleted;
	}

	public async cancelExchange(): Promise<void> {
		console.log('inventory:cancelExchange');
	}

	public async closeExchange(): Promise<void> {
		console.log('inventory:closeExchange');
	}

	public getInventorySlotsCount(): IObservable<{
		available: number;
		total: number;
	}> {
		return this.inventorySlotsCount;
	}

	public clearInventories(): void {
		console.log('clearInventories');
	}

	public getActiveItems(): IObservable<HeldItem[]> {
		return this.activeItems;
	}

	public getItemNameById(_itemId: string): string | null {
		return 'ItemName';
	}

	public getInventoryItems(): IObservable<
		IClientConfigs['inventory']['items']
	> {
		return this.inventoryItems;
	}

	public async exitMenu(): Promise<void> {
		console.log('InventoryServiceStub:exitMenu');
	}
}
